import { useEffect, useState } from 'react';
import { Loader2, Plus, X } from 'lucide-react';
import { Input, Label } from '@/shared/components/ui';
import type {
  CreateStorageLocationPayload,
  StorageLocationNode
} from '../Types';
import { formatApiErrorMessage } from '../Utils';
import {
  getCreatableChildLocationType,
  getLocationTypeLabel
} from '../Utils/StorageLocation.utils';

interface AddStorageLocationFormProps {
  parent: StorageLocationNode | null;
  submitting?: boolean;
  /** Skip the built-in trigger and render the fields inline; the caller owns visibility. */
  inline?: boolean;
  onCancel?: () => void;
  onCreate: (
    payload: CreateStorageLocationPayload
  ) => Promise<StorageLocationNode>;
  onCreated: (location: StorageLocationNode) => void;
}

export const AddStorageLocationForm = ({
  parent,
  submitting,
  inline = false,
  onCancel,
  onCreate,
  onCreated
}: AddStorageLocationFormProps) => {
  const locationType = getCreatableChildLocationType(parent);
  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!expanded) {
      setName('');
      setCode('');
      setError(null);
    }
  }, [expanded, parent?.id]);

  if (!locationType) return null;

  const typeLabel = getLocationTypeLabel(locationType);
  const actionLabel = parent
    ? `Add ${typeLabel.toLowerCase()} in ${parent.name}`
    : `Add ${typeLabel.toLowerCase()}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setError(null);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required');
      return;
    }

    const payload: CreateStorageLocationPayload = {
      name: trimmedName,
      locationType,
      ...(code.trim() ? { code: code.trim() } : {}),
      ...(parent ? { parentLocationId: parent.id } : {})
    };

    try {
      const created = await onCreate(payload);
      onCreated(created);
      setExpanded(false);
    } catch (err: unknown) {
      setError(
        formatApiErrorMessage(err, `Failed to add ${typeLabel.toLowerCase()}`)
      );
    }
  };

  if (inline) {
    return (
      <form
        onSubmit={handleSubmit}
        onClick={e => e.stopPropagation()}
        className="rounded-lg border border-[#087f8c]/30 bg-[#087f8c]/[0.05] p-2"
      >
        <p className="mb-1.5 text-[11px] font-semibold text-[#087f8c]">
          {actionLabel}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={`${typeLabel} name`}
            disabled={submitting}
            autoFocus
            className="h-9 min-w-[140px] flex-1 bg-white text-sm"
          />
          <Input
            value={code}
            onChange={e => setCode(e.target.value)}
            placeholder="Code"
            disabled={submitting}
            className="h-9 w-20 bg-white text-sm"
          />
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md bg-[#087f8c] px-3 text-xs font-bold text-white transition hover:bg-[#066873] disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
            Create
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => onCancel?.()}
            className="grid h-9 w-9 place-items-center rounded-md text-[#8a8f96] transition hover:bg-white hover:text-[#171717] disabled:opacity-50"
            aria-label="Cancel"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
      </form>
    );
  }

  if (!expanded) {
    return (
      <div className="border-t border-gray-200 bg-gray-50 p-1.5">
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            setExpanded(true);
          }}
          disabled={submitting}
          className={[
            'flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-[#006B82]/35',
            'bg-white px-3 py-2 text-sm font-medium text-[#006B82]',
            'hover:border-[#006B82]/60 hover:bg-[#006B82]/5',
            'disabled:pointer-events-none disabled:opacity-40',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#006B82]/30'
          ].join(' ')}
        >
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#006B82]/10">
            <Plus className="h-3 w-3" />
          </span>
          <span>{actionLabel}</span>
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-2 border-t border-gray-200 bg-gray-50 p-3"
      onClick={e => e.stopPropagation()}
    >
      <p className="text-xs font-semibold text-gray-600">{actionLabel}</p>
      <div>
        <Label className="text-xs text-gray-500 mb-1">{typeLabel} name *</Label>
        <Input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder={`e.g. ${locationType === 'room' ? 'Dry Store' : 'Shelf A'}`}
          disabled={submitting}
          autoFocus
          className="h-9 text-sm"
        />
      </div>
      <div>
        <Label className="text-xs text-gray-500 mb-1">Code (optional)</Label>
        <Input
          value={code}
          onChange={e => setCode(e.target.value)}
          placeholder="e.g. DRY"
          disabled={submitting}
          className="h-9 text-sm"
        />
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          disabled={submitting}
          onClick={() => setExpanded(false)}
          className="rounded-md px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-[#006B82] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#005a6e] disabled:opacity-50"
        >
          {submitting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Plus className="h-3.5 w-3.5" />
          )}
          Create {typeLabel.toLowerCase()}
        </button>
      </div>
    </form>
  );
};
